import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import {
  DarkMode,
  LightMode,
  Language,
  Notifications,
  Visibility,
  VisibilityOff,
  Lock,
  Save,
  Refresh,
  Palette,
  TextFields,
  Email,
  Sms,
  PhoneAndroid,
  DesktopWindows,
  Security,
  Check,
} from "@mui/icons-material";

const Settings = () => {
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Theme Settings
  const [themeSettings, setThemeSettings] = useState({
    darkMode: false,
    primaryColor: "#1976d2",
    accentColor: "#9c27b0",
  });

  // Language Settings
  const [languageSettings, setLanguageSettings] = useState({
    language: "en",
    dateFormat: "dd/MM/yyyy",
    timeFormat: "12h",
    timezone: "Asia/Kolkata",
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    assignmentAlerts: true,
    attendanceAlerts: true,
    examAlerts: true,
    leaveAlerts: true,
    chatNotifications: true,
    broadcastNotifications: true,
    marketingEmails: false,
  });

  // Display Settings
  const [displaySettings, setDisplaySettings] = useState({
    fontSize: 14,
    compactMode: false,
    showAvatars: true,
    animationsEnabled: true,
    tableRowsPerPage: 10,
    sidebarCollapsed: false,
  });

  // Password Change
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [passwordErrors, setPasswordErrors] = useState({});

  const languages = [
    { code: "en", name: "English" },
    { code: "hi", name: "हिन्दी (Hindi)" },
    { code: "mr", name: "मराठी (Marathi)" },
    { code: "ta", name: "தமிழ் (Tamil)" },
    { code: "te", name: "తెలుగు (Telugu)" },
    { code: "bn", name: "বাংলা (Bengali)" },
    { code: "gu", name: "ગુજરાતી (Gujarati)" },
    { code: "kn", name: "ಕನ್ನಡ (Kannada)" },
    { code: "ml", name: "മലയാളം (Malayalam)" },
    { code: "pa", name: "ਪੰਜਾਬੀ (Punjabi)" },
  ];

  const timezones = [
    { value: "Asia/Kolkata", label: "India Standard Time (IST)" },
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "Europe/London", label: "Greenwich Mean Time (GMT)" },
    { value: "Asia/Dubai", label: "Gulf Standard Time (GST)" },
    { value: "Asia/Singapore", label: "Singapore Time (SGT)" },
  ];

  const primaryColors = [
    { color: "#1976d2", name: "Blue" },
    { color: "#2e7d32", name: "Green" },
    { color: "#7b1fa2", name: "Purple" },
    { color: "#c62828", name: "Red" },
    { color: "#f57c00", name: "Orange" },
    { color: "#00838f", name: "Teal" },
    { color: "#5d4037", name: "Brown" },
    { color: "#455a64", name: "Blue Grey" },
  ];

  const handleThemeChange = (field, value) => {
    setThemeSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleLanguageChange = (field, value) => {
    setLanguageSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleNotificationChange = (field) => {
    setNotificationSettings((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleDisplayChange = (field, value) => {
    setDisplaySettings((prev) => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }));
    // Clear errors when typing
    if (passwordErrors[field]) {
      setPasswordErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validatePassword = () => {
    const errors = {};
    if (!passwordData.currentPassword) {
      errors.currentPassword = "Current password is required";
    }
    if (!passwordData.newPassword) {
      errors.newPassword = "New password is required";
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8 characters";
    } else if (
      !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordData.newPassword)
    ) {
      errors.newPassword =
        "Password must contain uppercase, lowercase, and number";
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordSubmit = () => {
    if (validatePassword()) {
      // API call to change password
      console.log("Changing password...");
      setSaveSuccess(true);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  };

  const handleSaveAll = () => {
    // API call to save all settings
    console.log("Saving settings:", {
      themeSettings,
      languageSettings,
      notificationSettings,
      displaySettings,
    });
    setSaveSuccess(true);
  };

  const handleResetDefaults = () => {
    setThemeSettings({
      darkMode: false,
      primaryColor: "#1976d2",
      accentColor: "#9c27b0",
    });
    setLanguageSettings({
      language: "en",
      dateFormat: "dd/MM/yyyy",
      timeFormat: "12h",
      timezone: "Asia/Kolkata",
    });
    setNotificationSettings({
      emailNotifications: true,
      smsNotifications: false,
      pushNotifications: true,
      assignmentAlerts: true,
      attendanceAlerts: true,
      examAlerts: true,
      leaveAlerts: true,
      chatNotifications: true,
      broadcastNotifications: true,
      marketingEmails: false,
    });
    setDisplaySettings({
      fontSize: 14,
      compactMode: false,
      showAvatars: true,
      animationsEnabled: true,
      tableRowsPerPage: 10,
      sidebarCollapsed: false,
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Customize your preferences and account settings
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleResetDefaults}
          >
            Reset to Default
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveAll}
          >
            Save All Changes
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Theme Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Palette color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Theme & Appearance
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {/* Dark Mode Toggle */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                  p: 2,
                  bgcolor: themeSettings.darkMode ? "grey.900" : "grey.100",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {themeSettings.darkMode ? (
                    <DarkMode sx={{ fontSize: 40 }} />
                  ) : (
                    <LightMode sx={{ fontSize: 40, color: "orange" }} />
                  )}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {themeSettings.darkMode ? "Dark Mode" : "Light Mode"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {themeSettings.darkMode
                        ? "Easy on the eyes in low light"
                        : "Bright and clear visibility"}
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  checked={themeSettings.darkMode}
                  onChange={(e) =>
                    handleThemeChange("darkMode", e.target.checked)
                  }
                  color="primary"
                />
              </Box>

              {/* Primary Color */}
              <Typography variant="subtitle2" gutterBottom>
                Primary Color
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
                {primaryColors.map((item) => (
                  <Box
                    key={item.color}
                    onClick={() =>
                      handleThemeChange("primaryColor", item.color)
                    }
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: item.color,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border:
                        themeSettings.primaryColor === item.color
                          ? "3px solid"
                          : "2px solid transparent",
                      borderColor:
                        themeSettings.primaryColor === item.color
                          ? "white"
                          : "transparent",
                      boxShadow:
                        themeSettings.primaryColor === item.color
                          ? `0 0 0 2px ${item.color}`
                          : "none",
                      "&:hover": { transform: "scale(1.1)" },
                      transition: "all 0.2s",
                    }}
                  >
                    {themeSettings.primaryColor === item.color && (
                      <Check sx={{ color: "white", fontSize: 20 }} />
                    )}
                  </Box>
                ))}
              </Box>

              <Typography variant="caption" color="text.secondary">
                Selected:{" "}
                {
                  primaryColors.find(
                    (c) => c.color === themeSettings.primaryColor
                  )?.name
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Language & Region */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Language color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Language & Region
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Language</InputLabel>
                    <Select
                      value={languageSettings.language}
                      label="Language"
                      onChange={(e) =>
                        handleLanguageChange("language", e.target.value)
                      }
                    >
                      {languages.map((lang) => (
                        <MenuItem key={lang.code} value={lang.code}>
                          {lang.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Date Format</InputLabel>
                    <Select
                      value={languageSettings.dateFormat}
                      label="Date Format"
                      onChange={(e) =>
                        handleLanguageChange("dateFormat", e.target.value)
                      }
                    >
                      <MenuItem value="dd/MM/yyyy">dd/MM/yyyy</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Time Format</InputLabel>
                    <Select
                      value={languageSettings.timeFormat}
                      label="Time Format"
                      onChange={(e) =>
                        handleLanguageChange("timeFormat", e.target.value)
                      }
                    >
                      <MenuItem value="12h">12 Hour (AM/PM)</MenuItem>
                      <MenuItem value="24h">24 Hour</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={languageSettings.timezone}
                      label="Timezone"
                      onChange={(e) =>
                        handleLanguageChange("timezone", e.target.value)
                      }
                    >
                      {timezones.map((tz) => (
                        <MenuItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Notifications color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Notification Preferences
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {/* Notification Channels */}
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                Notification Channels
              </Typography>
              <List dense sx={{ mb: 2 }}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Email
                      color={
                        notificationSettings.emailNotifications
                          ? "primary"
                          : "disabled"
                      }
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Email Notifications"
                    secondary="Receive updates via email"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={notificationSettings.emailNotifications}
                      onChange={() =>
                        handleNotificationChange("emailNotifications")
                      }
                      size="small"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Sms
                      color={
                        notificationSettings.smsNotifications
                          ? "primary"
                          : "disabled"
                      }
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="SMS Notifications"
                    secondary="Receive updates via SMS"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={notificationSettings.smsNotifications}
                      onChange={() =>
                        handleNotificationChange("smsNotifications")
                      }
                      size="small"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PhoneAndroid
                      color={
                        notificationSettings.pushNotifications
                          ? "primary"
                          : "disabled"
                      }
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Push Notifications"
                    secondary="Receive in-app notifications"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={notificationSettings.pushNotifications}
                      onChange={() =>
                        handleNotificationChange("pushNotifications")
                      }
                      size="small"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>

              {/* Alert Types */}
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                Alert Types
              </Typography>
              <Grid container spacing={1}>
                {[
                  { key: "assignmentAlerts", label: "Assignments" },
                  { key: "attendanceAlerts", label: "Attendance" },
                  { key: "examAlerts", label: "Exams" },
                  { key: "leaveAlerts", label: "Leave Updates" },
                  { key: "chatNotifications", label: "Chat Messages" },
                  { key: "broadcastNotifications", label: "Broadcasts" },
                ].map((item) => (
                  <Grid item xs={6} key={item.key}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={notificationSettings[item.key]}
                          onChange={() => handleNotificationChange(item.key)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">{item.label}</Typography>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Display Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <DesktopWindows color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Display Preferences
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {/* Font Size */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <TextFields />
                  <Typography variant="subtitle2">
                    Font Size: {displaySettings.fontSize}px
                  </Typography>
                </Box>
                <Slider
                  value={displaySettings.fontSize}
                  onChange={(e, value) =>
                    handleDisplayChange("fontSize", value)
                  }
                  min={12}
                  max={20}
                  marks={[
                    { value: 12, label: "Small" },
                    { value: 14, label: "Default" },
                    { value: 16, label: "Medium" },
                    { value: 18, label: "Large" },
                    { value: 20, label: "X-Large" },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>

              {/* Table Rows Per Page */}
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Table Rows Per Page</InputLabel>
                  <Select
                    value={displaySettings.tableRowsPerPage}
                    label="Table Rows Per Page"
                    onChange={(e) =>
                      handleDisplayChange("tableRowsPerPage", e.target.value)
                    }
                  >
                    <MenuItem value={5}>5 rows</MenuItem>
                    <MenuItem value={10}>10 rows</MenuItem>
                    <MenuItem value={25}>25 rows</MenuItem>
                    <MenuItem value={50}>50 rows</MenuItem>
                    <MenuItem value={100}>100 rows</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Toggle Options */}
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={displaySettings.compactMode}
                        onChange={(e) =>
                          handleDisplayChange("compactMode", e.target.checked)
                        }
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">Compact Mode</Typography>
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={displaySettings.showAvatars}
                        onChange={(e) =>
                          handleDisplayChange("showAvatars", e.target.checked)
                        }
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">Show Avatars</Typography>
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={displaySettings.animationsEnabled}
                        onChange={(e) =>
                          handleDisplayChange(
                            "animationsEnabled",
                            e.target.checked
                          )
                        }
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Animations</Typography>}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={displaySettings.sidebarCollapsed}
                        onChange={(e) =>
                          handleDisplayChange(
                            "sidebarCollapsed",
                            e.target.checked
                          )
                        }
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">Sidebar Collapsed</Typography>
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Password Change */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Security color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Change Password
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Current Password"
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    handlePasswordChange("currentPassword", e.target.value)
                  }
                  error={!!passwordErrors.currentPassword}
                  helperText={passwordErrors.currentPassword}
                  size="small"
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowCurrentPassword(!showCurrentPassword)
                          }
                          edge="end"
                          size="small"
                        >
                          {showCurrentPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="New Password"
                  type={showNewPassword ? "text" : "password"}
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    handlePasswordChange("newPassword", e.target.value)
                  }
                  error={!!passwordErrors.newPassword}
                  helperText={
                    passwordErrors.newPassword ||
                    "Min 8 chars, uppercase, lowercase, number"
                  }
                  size="small"
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                          size="small"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Confirm New Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    handlePasswordChange("confirmPassword", e.target.value)
                  }
                  error={!!passwordErrors.confirmPassword}
                  helperText={passwordErrors.confirmPassword}
                  size="small"
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  variant="contained"
                  startIcon={<Lock />}
                  onClick={handlePasswordSubmit}
                  disabled={
                    !passwordData.currentPassword ||
                    !passwordData.newPassword ||
                    !passwordData.confirmPassword
                  }
                >
                  Update Password
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Security Info */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Lock color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Account Security
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <List dense>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Last Password Change"
                    secondary="December 15, 2025"
                  />
                  <Chip label="30 days ago" size="small" color="success" />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Two-Factor Authentication"
                    secondary="Add an extra layer of security"
                  />
                  <Chip label="Not Enabled" size="small" color="warning" />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Active Sessions"
                    secondary="Devices currently logged in"
                  />
                  <Chip label="2 devices" size="small" />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Login History"
                    secondary="View recent login activity"
                  />
                  <Button size="small" variant="outlined">
                    View
                  </Button>
                </ListItem>
              </List>

              <Box sx={{ mt: 2 }}>
                <Button variant="outlined" color="error" fullWidth>
                  Sign Out All Devices
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Success Snackbar */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setSaveSuccess(false)}>
          Settings saved successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
